import React from "react";
import LegalShell, { Section } from "@/pages/legal/LegalShell";

const UPDATED = "16 January 2026";

export default function Privacy() {
  return (
    <LegalShell eyebrow="§ Legal · 02" title={<>Privacy <span className="saffron">Policy.</span></>} updated={UPDATED}>
      <p>
        Gritly Analytics Private Limited (&quot;Company&quot;, &quot;we&quot;) operates the Forte² Space platform. This Privacy
        Policy explains how we collect, use, share and protect your personal data in compliance with the
        <strong className="text-white"> Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>, the
        Information Technology Act, 2000 and the SPDI Rules, 2011.
      </p>

      <Section n="1" title="Data We Collect">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong className="text-white">Identity</strong>: name, email, profile picture (via Google sign-in).</li>
          <li><strong className="text-white">Assessment</strong>: answers you submit, self-declared background and goals.</li>
          <li><strong className="text-white">Usage</strong>: pages visited, features used, device/browser metadata.</li>
          <li><strong className="text-white">Contact</strong>: messages sent through the contact form or via email.</li>
        </ul>
      </Section>

      <Section n="2" title="Purpose &amp; Legal Basis">
        <p>We process personal data solely for the purposes below, on the basis of your consent under the DPDP
        Act or, where applicable, legitimate use as defined therein:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Deliver and improve the Platform, including generating your Forte² Score and AI-authored reports.</li>
          <li>Respond to contact messages and support requests.</li>
          <li>Send transactional emails (e.g., waitlist and contact confirmations).</li>
          <li>Comply with legal obligations under Indian law.</li>
        </ul>
      </Section>

      <Section n="3" title="How We Share Data">
        <p>We do not sell personal data. We share personal data only with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong className="text-white">Google</strong> — authentication.</li>
          <li><strong className="text-white">Emergent / LLM providers</strong> — to generate AI reports. Only structured, minimum-necessary data is transmitted.</li>
          <li><strong className="text-white">Resend</strong> — to deliver transactional email to [email protected].</li>
          <li><strong className="text-white">Government authorities</strong> — where required by valid legal process under Indian law.</li>
        </ul>
      </Section>

      <Section n="4" title="Data Retention">
        <p>We retain personal data only for as long as necessary to fulfil the purposes for which it was
        collected, and thereafter for periods mandated by applicable Indian law. Assessment reports are
        retained for the lifetime of your account or until you request deletion.</p>
      </Section>

      <Section n="5" title="Your Rights under the DPDP Act">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong className="text-white">Right to Access</strong> the personal data we hold about you.</li>
          <li><strong className="text-white">Right to Correction</strong> of inaccurate or outdated data.</li>
          <li><strong className="text-white">Right to Erasure</strong> of your personal data.</li>
          <li><strong className="text-white">Right to Grievance Redressal</strong>.</li>
          <li><strong className="text-white">Right to Nominate</strong> another person to exercise these rights.</li>
          <li><strong className="text-white">Right to Withdraw Consent</strong> at any time.</li>
        </ul>
        <p>To exercise any of these, email <span className="saffron">[email protected]</span> with the
        subject &quot;DPDP Request&quot;. We will respond within 30 days.</p>
      </Section>

      <Section n="6" title="Children">
        <p>The Platform is not directed at children under 13. For users under 18, we require verifiable
        parental or guardian consent as prescribed under the DPDP Act. We do not run targeted advertising or
        behavioural tracking on data known to belong to a child.</p>
      </Section>

      <Section n="7" title="Security Safeguards">
        <p>We implement reasonable security practices including HTTPS/TLS in transit, encrypted storage,
        role-based access controls, secure cookies (httpOnly, secure, SameSite=None) and audit logging, in
        line with SPDI Rules, 2011 and the DPDP Act.</p>
      </Section>

      <Section n="8" title="Cross-Border Transfers">
        <p>Some sub-processors (e.g., Anthropic, Resend) may process data outside India. Such transfers occur
        only to jurisdictions not restricted by the Government of India and under appropriate contractual
        safeguards. We do not accept or process cross-border payments at this time.</p>
      </Section>

      <Section n="9" title="Cookies">
        <p>We use strictly necessary cookies (session_token) for authentication. We do not use third-party
        advertising cookies. You can block cookies via your browser, but the Platform may not function.</p>
      </Section>

      <Section n="10" title="Grievance Officer">
        <p>Data Protection Officer / Grievance Officer:<br/>
        Gritly Analytics Private Limited<br/>
        Chennai, Tamil Nadu, India<br/>
        Email: <span className="saffron">[email protected]</span> (subject: &quot;DPDP Request&quot; or &quot;Grievance&quot;)</p>
      </Section>

      <Section n="11" title="Changes">
        <p>We may update this Privacy Policy from time to time. Material changes will be notified via email
        or an in-app banner at least 7 days before taking effect.</p>
      </Section>
    </LegalShell>
  );
}
